import type {
  TriaxialGeneral,
  TriaxialSamplePrepMethod,
  TriaxialTrimmingProcedure,
  TriaxialSideDrainsType,
  TriaxialAreaMethod,
  TriaxialFailureCriterion,
} from '../../../types/triaxial';

/** Blank starting state for a brand-new triaxial test. */
export const EMPTY_TRIAXIAL_GENERAL: TriaxialGeneral = {
  testType: 'CIU',
  testId: null,
  stationNumber: null,
  diameter1: null, diameter2: null, diameter3: null,
  diameter4: null, diameter5: null, diameter6: null,
  length1: null, length2: null, length3: null,
  length4: null, length5: null, length6: null,
  computedAvgDiameterMm: null,
  computedAvgLengthMm: null,
  computedCalcDiameterMm: null,

  samplePrepMethod: null,
  samplePrepOtherText: null,
  trimmingProcedure: null,
  specificGravityGs: null,
  gsAssumedOrMeasured: null,
  membraneStiffnessKPa: 1400,
  wallThicknessMm: null,
  perimeterMm: null,
  sideDrainsUsed: false,
  sideDrainsType: null,
  filterPaperLoadKnPerMm: 0.00019,
  filterPaperPerimeterCoverageMm: null,

  massOfWetSampleG: null,
  massOfPanG: null,
  massWetSamplePlusPanG: null,
  massDrySamplePlusPanG: null,
  massExtraG: null,
  initialVolumeMm3: null,
  computedInitialBulkDensityMgPerM3: null,
  computedInitialDryDensityMgPerM3: null,
  computedInitialWaterContentPct: null,
  computedInitialVoidRatio: null,
  computedInitialDegreeOfSatPct: null,

  finalWaterContentPct: null,
  finalWetMassG: null,
  finalDryMassG: null,

  initialDeformationMm: null,
  endOfSaturationDeformationMm: null,
  computedDeformationDuringSaturationMm: null,
  bValue: null,

  consolidationFromStage: null,
  consolidationToStage: null,
  backPressureAppliedKPa: null,
  averagePwpDuringConsolidationKPa: null,
  areaCalculationMethod: 'A',
  computedAreaMethodAM2: null,
  computedAreaMethodBM2: null,

  shearStageNumber: null,
  calibratedCellPressureKPa: null,
  shearRateMmPerMin: null,
  computedShearStrainRatePctPerHr: null,
  failureCriterion: 'MaxDeviatorStress',
  failureCriterionOtherText: null,

  applyMembraneCorrection: true,
  applyFilterPaperCorrection: true,
  applySideDrainCorrection: true,

  overrideLL: null,
  overridePL: null,
  overridePI: null,
  overrideUSCSDescription: null,
  overrideGravelPct: null,
  overrideSandPct: null,
  overrideSiltPct: null,
  overrideClayPct: null,
  overrideFinesPct: null,

  maxPointStrainPct: null,
  maxPointDeviatorKPa: null,
  maxPointPwpKPa: null,
  endPointStrainPct: null,
  endPointDeviatorKPa: null,
  endPointPwpKPa: null,
  peakPointStrainPct: null,
  peakPointDeviatorKPa: null,
  residualPointStrainPct: null,
  residualPointDeviatorKPa: null,

  computedSkemptonA: null,
  computedConsolidationStressKPa: null,
  computedTauMaxKPa: null,
  computedTauEndKPa: null,
  computedPhiMaxDeg: null,
  computedPhiEndDeg: null,
  computedPostConsHeightCm: null,
  computedPostConsDiameterCm: null,
  computedPostConsWaterContentPct: null,
  computedPostConsBulkDensityMgPerM3: null,
  computedPostConsDryDensityMgPerM3: null,
  computedPostConsVoidRatio: null,
  computedPostConsDegreeOfSatPct: null,
  computedFinalHeightCm: null,
  computedFinalDiameterCm: null,
  computedFinalBulkDensityMgPerM3: null,
  computedFinalDryDensityMgPerM3: null,
  computedFinalVoidRatio: null,
  computedFinalDegreeOfSatPct: null,

  comments: null,
};

export const SAMPLE_PREP_OPTIONS: Array<{ value: TriaxialSamplePrepMethod; label: string }> = [
  { value: 'Intact',                       label: 'In-situ Sample' },
  { value: 'Remoulded',                    label: 'Remoulded' },
  { value: 'ReconstitutedMoistTamping',    label: 'Reconstituted by Moist Tamping' },
  { value: 'ReconstitutedAirDriedSlurry',  label: 'Reconstituted by Air Dried Slurry' },
  { value: 'ReconstitutedOther',           label: 'Reconstituted (other — specify)' },
  { value: 'AsReceived',                   label: 'As Received' },
];

export const TRIMMING_OPTIONS: Array<{ value: TriaxialTrimmingProcedure; label: string }> = [
  { value: 'Turntable',        label: 'Turntable' },
  { value: 'CuttingShoe',      label: 'Cutting Shoe' },
  { value: 'DirectTest',       label: 'Direct Test (no trimming)' },
  { value: 'RingLinedSampler', label: 'Ring-Lined Sampler' },
];

export const SIDE_DRAINS_TYPE_OPTIONS: Array<{ value: TriaxialSideDrainsType; label: string }> = [
  { value: 'Vertical', label: 'Vertical' },
  { value: 'Spiral',   label: 'Spiral' },
];

export const AREA_METHOD_OPTIONS: Array<{ value: TriaxialAreaMethod; label: string }> = [
  { value: 'A',    label: 'Method A (V/H)' },
  { value: 'B',    label: 'Method B (water content)' },
  { value: 'Both', label: 'Both (averaged)' },
];

export const FAILURE_CRITERION_OPTIONS: Array<{ value: TriaxialFailureCriterion; label: string }> = [
  { value: 'MaxDeviatorStress',      label: 'Maximum deviator stress' },
  { value: 'DeviatorStressAt15Pct',  label: 'Deviator stress at 15 % strain' },
  { value: 'MaxEffStressRatio',      label: "Maximum effective stress ratio (σ'₁/σ'₃)" },
  { value: 'Other',                  label: 'Other (specify)' },
];
